/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { FloodgateApi } from "api/FloodgateApi";
import gatewayFetch from "utilities/gatewayFetch";

const isTest = JSON.parse(process.env.IS_TEST ?? "false");
const isSnapshot = navigator.userAgent === "ReactSnap";
const CUSTOMER_IO_WRITE_KEY = process.env.CUSTOMER_IO_WRITE_KEY;
const CUSTOMER_IO_SITE_ID = process.env.CUSTOMER_IO_SITE_ID;

let _cioanalytics: any = {
  page: () => {},
  track: (event: PersonalizationEvent) => {
    console.debug(`CustomerIO Tracking is off: Event: ${event}`);
  },
  identify: () => {},
};

let _ajoanalytics: any = {
  track: (event: PersonalizationEvent) => {
    console.debug(`AJO Tracking is off: Event: ${event}`);
  },
};

export enum PersonalizationEvent {
  EXPRESS_PREMIUM_CHECKOUT_COMPLETED = "express_premium_checkout_completed",
  EXPRESS_PREMIUM_CHECKOUT_CANCELLED = "express_premium_checkout_cancelled",
  EXPRESS_PREMIUM_CHECKOUT_STARTED = "express_premium_checkout_started",
  STUDIO_RECORDING_SUCCESS = "studio_recording_success",
  USER_CREATES_PROJECT = "user_creates_project",
  USER_ENHANCES_SPEECH = "user_enhances_speech",
  USER_EXPORTS_PROJECT = "user_exports_project",
  USER_TRANSCRIBES_FILE = "user_transcribes_file",
  USER_UPLOADS_FILE = "user_uploads_file",
  ENHANCE_SPEECH_INFLUENCER_TRY_NOW_CLICKED = "enhance_speech_influencer_try_now_clicked",
  // C.IO only for now, will drop "express_" when we drop AJO
  PODCAST_PREMIUM_CHECKOUT_COMPLETED = "podcast_premium_checkout_completed",
  PODCAST_PREMIUM_CHECKOUT_CANCELLED = "podcast_premium_checkout_cancelled",
  PODCAST_PREMIUM_CHECKOUT_STARTED = "podcast_premium_checkout_started",
}

// https://wiki.corp.adobe.com/display/marketingtech/Adobe+Experience+Platform+Integration+Guide
// See app/frontend/index.html that adds `window._satellite`
declare global {
  interface Window {
    _satellite: {
      track: (command: string, config: any) => void;
    };
  }
}

const injectAjoTag = (): Promise<void> => {
  const isTest = JSON.parse(process.env.IS_TEST ?? "false");
  const isSnapshot = navigator.userAgent === "ReactSnap";
  if (!isTest && process.env.AJO_LAUNCH_URL && !isSnapshot) {
    const script = document.createElement("script");
    script.src = "https://www.adobe.com/marketingtech/main.standard.min.js";
    document.body.appendChild(script);
    return new Promise((resolve) => {
      script.onload = (): void => {
        window._satellite.track("pageload", {
          xdm: {},
          data: {},
        });
        resolve();
      };
    });
  } else {
    window._satellite = {
      track: (): void => {},
    };
    return Promise.resolve();
  }
};

export const loadCustomerPersonalization = async (): Promise<void> => {
  // Load Customer.io analytics if enabled
  const isCustomerIoEnabledFF = await FloodgateApi.instance
    .getFeatureEnabled("customer_io_enabled")
    [Symbol.asyncIterator]()
    .next();
  const isCustomerIoEnabled = isCustomerIoEnabledFF.value;

  // Load Customer.IO Tracking
  if (
    !isTest &&
    !isSnapshot &&
    isCustomerIoEnabled &&
    CUSTOMER_IO_WRITE_KEY &&
    CUSTOMER_IO_SITE_ID
  ) {
    const cioSdk = AnalyticsBrowser.load(
      { writeKey: CUSTOMER_IO_WRITE_KEY },
      {
        integrations: {
          "Customer.io In-App Plugin": {
            siteId: CUSTOMER_IO_SITE_ID,
          },
        },
      }
    );
    _cioanalytics = {
      track: cioSdk.track,
      identify: (email: string): void => {
        cioSdk.identify(email);
        gatewayFetch("/api/v1/customer_personalization/enrichment", {
          method: "POST",
        });
      },
    };
    cioSdk.page();
  }

  // Load AJO analytics
  if (!isTest && !isSnapshot) {
    injectAjoTag();
    _ajoanalytics.track = (event: PersonalizationEvent): void => {
      try {
        window._satellite.track("event", {
          xdm: {},
          data: {
            web: {
              webInteraction: {
                name: `podcast/${event}`,
                URL: window.location.href,
              },
            },
            _adobe_corpnew: {
              podcastMetadata: {},
            },
          },
        });
      } catch (error) {
        console.error("Something went wrong with an AJO request", error);
      }
    };
  }
};

// Identify the customer in our personalization services.
export const identifyCustomer = (email: string): void => {
  _cioanalytics.identify(email);
  // NOTE: AJO SDK has integrations with IMS so no need to identify
};

const mapCheckoutEventsForCustomerIO = (
  event: PersonalizationEvent
): PersonalizationEvent => {
  switch (event) {
    case PersonalizationEvent.EXPRESS_PREMIUM_CHECKOUT_STARTED:
      return PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_STARTED;
    case PersonalizationEvent.EXPRESS_PREMIUM_CHECKOUT_CANCELLED:
      return PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_CANCELLED;
    case PersonalizationEvent.EXPRESS_PREMIUM_CHECKOUT_COMPLETED:
      return PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_COMPLETED;
    default:
      return event;
  }
};

// Track events in our personalization services.
export const trackEvent = (event: PersonalizationEvent): void => {
  _cioanalytics.track(mapCheckoutEventsForCustomerIO(event));
  _ajoanalytics.track(event);
};
